import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import createContextFactory from '@Utils/factory'
import history from '@Utils/history'

import { BULK_PUBLISH_PARAM, MESSAGE_SOURCE } from './protocol'

const PUBLISH_TIMEOUT = 60000

export const IDLE = 'idle'
export const RUNNING = 'running'
export const FINISHED = 'finished'

function useBulkPublishFactory() {
  const [status, setStatus] = useState(IDLE)
  const [jobs, setJobs] = useState([])
  const [source, setSource] = useState(null)

  const iframeRef = useRef(null)
  const jobsRef = useRef(jobs)
  const currentRef = useRef(null)
  const timeoutRef = useRef(null)
  const readyRef = useRef(false)

  jobsRef.current = jobs

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const setJobStatus = useCallback((id, jobStatus, error = null) => {
    setJobs(jobs => jobs.map(job => job.id === id ? { ...job, status: jobStatus, error } : job))
  }, [])

  const reloadRunner = useCallback(() => {
    readyRef.current = false
    currentRef.current = null
    setSource(source => source ? { ...source, nonce: Date.now() } : source)
  }, [])

  const publishNext = useCallback(() => {
    if (!readyRef.current || currentRef.current) {
      return
    }

    const next = jobsRef.current.find(({ status }) => status === 'pending')

    if (!next) {
      setStatus(status => status === RUNNING ? FINISHED : status)
      return
    }

    const { contentWindow } = iframeRef.current || {}
    if (!contentWindow) {
      return
    }

    currentRef.current = next.id
    setJobStatus(next.id, 'publishing')
    contentWindow.postMessage({ source: MESSAGE_SOURCE, type: 'publish', id: next.id }, window.location.origin)

    clearTimer()
    timeoutRef.current = setTimeout(() => {
      setJobStatus(next.id, 'error', 'Timed out')
      reloadRunner()
    }, PUBLISH_TIMEOUT)
  }, [setJobStatus, clearTimer, reloadRunner])

  useEffect(() => {
    const handleMessage = event => {
      if (event.origin !== window.location.origin) return
      if (event.source !== iframeRef.current?.contentWindow) return
      if (event.data?.source !== MESSAGE_SOURCE) return

      const { type, id, ok, error } = event.data

      if (type === 'ready') {
        readyRef.current = true
        publishNext()
        return
      }

      if (type === 'result') {
        if (currentRef.current !== id) return
        clearTimer()
        currentRef.current = null
        setJobStatus(id, ok ? 'done' : 'error', ok ? null : error || 'Publish failed')
        publishNext()
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [publishNext, setJobStatus, clearTimer])

  useEffect(() => () => clearTimer(), [clearTimer])

  useEffect(() => {
    if (status === RUNNING) {
      publishNext()
    }
  }, [jobs, status, publishNext])

  const start = useCallback(({ contentType, entries }) => {
    if (!entries.length) return

    clearTimer()
    currentRef.current = null
    readyRef.current = false
    setJobs(entries.map(entry => ({ ...entry, status: 'pending', error: null })))
    setSource({ contentType, firstId: entries[0].id, nonce: Date.now() })
    setStatus(RUNNING)
  }, [clearTimer])

  const close = useCallback(() => {
    clearTimer()
    currentRef.current = null
    readyRef.current = false
    setStatus(IDLE)
    setJobs([])
    setSource(null)
  }, [clearTimer])

  const summary = useMemo(() => {
    const published = jobs.filter(({ status }) => status === 'done').length
    const errors = jobs.filter(({ status }) => status === 'error')
    const current = jobs.find(({ status }) => status === 'publishing')

    return {
      total: jobs.length,
      published,
      failed: errors.length,
      errors,
      current,
      position: Math.min(published + errors.length + (current ? 1 : 0), jobs.length),
    }
  }, [jobs])

  const iframeSrc = useMemo(() => {
    if (!source) return null

    const { contentType, firstId, nonce } = source
    return history.createHref({
      pathname: `/cms/${contentType}/${firstId}/builder`,
      search: `?${BULK_PUBLISH_PARAM}=1&t=${nonce}`,
    })
  }, [source])

  return useMemo(() => ({
    status, jobs, summary, start, close, iframeRef, iframeSrc,
  }), [status, jobs, summary, start, close, iframeSrc])
}

export const {
  Provider: BulkPublishStateProvider,
  useContext: useBulkPublish,
} = createContextFactory(useBulkPublishFactory)
